import { useState } from 'react'
import { ChevronUp, ChevronDown } from 'lucide-react'
import { fmtMoneda } from '@/lib/cd2bPricing'
import { referenciaLocal, usd } from '@/lib/pais'
import { cn } from '@/lib/utils'
import { resumenAnual } from '@/lib/cicloPago'

const MOTIVOS_COMISION = {
  PAIS_REQUERIDO: 'Elige el país para ver tu comisión.',
  VINCULO_NO_DEFINIDO: 'Tu comisión aparece cuando el administrador defina tu tipo de asesor.',
  POLITICA_NO_CONFIGURADA: 'Este país aún no tiene política comercial: no se puede cobrar ni comisionar todavía.',
}

function Fila({ label, children, fuerte }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={cn('text-body-sm', fuerte ? 'font-semibold text-neutral-900 dark:text-neutral-50' : 'text-subtle')}>{label}</span>
      <span className={cn('tabular-nums text-right', fuerte ? 'text-h3 font-bold text-primary-700 dark:text-primary-300' : 'text-body-sm text-neutral-800 dark:text-neutral-200')}>
        {children}
      </span>
    </div>
  )
}

/** La comisión estimada con la POLÍTICA DEL PAÍS (docs/179), o el motivo de que no haya número. */
function Comision({ comision, moneda, anual = false }) {
  if (comision?.detalle?.inicial > 0) {
    const d = comision.detalle
    // Pago anual: la comisión inicial se calcula sobre el año cobrado, con el mismo porcentaje.
    const inicial = anual && comision.anual?.comisionInicial != null ? comision.anual.comisionInicial : d.inicial
    return (
      <div className="rounded-md bg-accent-50 p-3 dark:bg-accent-900/30">
        <p className="text-caption font-semibold uppercase tracking-wide text-accent-700 dark:text-accent-300">Tu comisión estimada</p>
        <p className="font-mono text-h3 font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">{usd(inicial)}</p>
        <p className="text-caption text-neutral-600 dark:text-neutral-400">
          {anual
            ? `${d.inicialPct} % del pago anual. `
            : `${d.inicialPct} % del primer mes, luego ${usd(d.recurrente)} al mes${d.mesesRecurrencia ? ` × ${d.mesesRecurrencia}` : ' mientras pague'}. `}
          Política de {d.pais} v{d.version}.
        </p>
      </div>
    )
  }
  if (comision?.detalle?.motivo) {
    return (
      <p className="text-caption text-subtle">
        {MOTIVOS_COMISION[comision.detalle.motivo] || 'No se puede estimar tu comisión.'}
      </p>
    )
  }
  if (comision && comision.comisionMensual > 0) {
    return (
      <Fila label="Tu comisión estimada / mes">
        <span className="font-medium text-success-700 dark:text-success-300">{fmtMoneda(comision.comisionMensual, moneda)}</span>
      </Fila>
    )
  }
  return null
}

/**
 * El precio a la vista mientras se arma el paquete: mensual, impuesto del país, total y la
 * comisión. En escritorio es una columna fija a la derecha; en el teléfono, una barra abajo que
 * se despliega (la columna no cabe en 375 px y el precio no puede quedar tres pantallas arriba).
 *
 * El impuesto es el que publica la API para el país (`/public/cd2b/paises`). El cobro real lo
 * calcula el enlace de pago —un cliente que paga por Stripe internacional no lleva IVA—, así que
 * aquí se rotula como estimado.
 */
export default function ResumenFijo({ c }) {
  const { form, quote, comision, impuesto, catalogo } = c
  const [abierto, setAbierto] = useState(false)

  const mensual = Number(quote.totalMensual) || 0
  const anualActivo = resumenAnual(form.ciclo, comision?.anual)
  // Con el pago anual el impuesto y el total se calculan sobre el AÑO que se cobra, no sobre el mes:
  // el dueño ve lo que de verdad va a pagar.
  const base = anualActivo ? anualActivo.total : mensual
  const periodo = anualActivo ? 'al año' : 'al mes'
  const montoImpuesto = impuesto?.tasa != null ? Math.round(base * impuesto.tasa * 100) / 100 : null
  const total = montoImpuesto != null ? base + montoImpuesto : null
  const enUsd = String(form.moneda || '').toUpperCase() === 'USD'
  const refLocal = enUsd && total != null ? referenciaLocal(total, form.pais) : null
  const edicion = (catalogo.ediciones || []).find((e) => e.edicion === form.edicion)?.label || form.edicion

  const cuerpo = (
    <div className="space-y-2">
      <p className="text-caption text-subtle">
        {edicion} · {form.modulosSel.length} {form.modulosSel.length === 1 ? 'módulo' : 'módulos'}
      </p>
      <Fila label="Precio mensual">{fmtMoneda(mensual, form.moneda)}</Fila>
      {anualActivo && (
        <>
          <Fila label="Pago anual (un año por adelantado)">{fmtMoneda(anualActivo.total, form.moneda)}</Fila>
          {anualActivo.ahorro > 0 && <p className="text-right text-caption text-subtle">Ahorro frente a 12 meses: {fmtMoneda(anualActivo.ahorro, form.moneda)}</p>}
        </>
      )}
      <Fila label={impuesto?.tasa != null ? `${impuesto.nombre} ${+(impuesto.tasa * 100).toFixed(2)} %` : 'Impuesto'}>
        {!form.pais
          ? <span className="text-caption text-subtle">Elige el país</span>
          : montoImpuesto != null
            ? fmtMoneda(montoImpuesto, form.moneda)
            : <span className="text-caption text-subtle">{impuesto?.texto || 'Sin dato del país'}</span>}
      </Fila>
      <div className="border-t border-line pt-2">
        <Fila label={`Total ${periodo}`} fuerte>{fmtMoneda(total ?? base, form.moneda)}</Fila>
        {refLocal && <p className="text-right text-caption text-subtle">{refLocal}</p>}
        {total != null && <p className="text-caption text-subtle">Con impuesto, estimado: el enlace de pago confirma el cobro.</p>}
      </div>
      {quote.totalUnico > 0 && (
        <Fila label="Cargos únicos">{fmtMoneda(quote.totalUnico, form.moneda)}</Fila>
      )}
      <Comision comision={comision} moneda={form.moneda} anual={Boolean(anualActivo)} />
    </div>
  )

  return (
    <>
      {/* Escritorio: columna fija */}
      <aside className="hidden lg:block lg:sticky lg:top-20" aria-label="Resumen de la cotización">
        <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
          <p className="mb-3 text-body-sm font-semibold text-neutral-900 dark:text-neutral-50">Resumen</p>
          {cuerpo}
        </div>
      </aside>

      {/* Teléfono y tableta: barra inferior plegable, encima de la navegación inferior */}
      <div className="lg:hidden">
        <div className="h-20" aria-hidden="true" />
        <div className="fixed inset-x-0 z-40 bottom-[calc(56px+env(safe-area-inset-bottom))] md:bottom-0 border-t border-line bg-surface shadow-bottom-bar">
          <button
            type="button"
            onClick={() => setAbierto((v) => !v)}
            aria-expanded={abierto}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
          >
            <span className="min-w-0">
              <span className="block text-caption text-subtle">Total {periodo}{total != null ? ' con impuesto' : ''}</span>
              <span className="block text-body font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">
                {fmtMoneda(total ?? base, form.moneda)}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1 text-caption font-medium text-primary">
              {abierto ? 'Ocultar' : 'Ver detalle'}
              {abierto ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </span>
          </button>
          {abierto && <div className="max-h-[50vh] overflow-y-auto border-t border-line px-4 pb-4 pt-3">{cuerpo}</div>}
        </div>
      </div>
    </>
  )
}
